import { ApiError } from "./client";

/**
 * Session-expiry guards for heartbeat-style pollers.
 *
 * Several staff surfaces poll on an interval (unread counts, live triage
 * queues, AI-status watchers). When a user leaves a tab open long enough for
 * the auth cookie to expire, every subsequent tick 401s — a session that will
 * never recover on its own, flooding the server logs with 401s. These helpers
 * let a poll loop stop ticking (and stop retrying) once the session is gone;
 * the user lands on the login redirect the next time they navigate.
 */

/** True when a request failed because the session expired (HTTP 401). */
export function isSessionExpired(error: unknown): boolean {
  return error instanceof ApiError && error.status === 401;
}

/**
 * Builds a React Query `refetchInterval` that polls every `intervalMs` but
 * stops permanently once a request 401s.
 *
 * Use for constant-interval pollers. Pollers whose interval depends on the
 * fetched data should keep their own callback and short-circuit with
 * `isSessionExpired(query.state.error)` at the top instead.
 */
export function pausePollingOnSessionExpiry(
  intervalMs: number,
): (query: { state: { error: unknown } }) => number | false {
  return (query) => (isSessionExpired(query.state.error) ? false : intervalMs);
}

/**
 * React Query `retry` predicate: retry transient failures up to 3 times, but
 * give up immediately on a 401 — an expired session won't be fixed by retrying.
 */
export function retryUnlessSessionExpired(
  failureCount: number,
  error: unknown,
): boolean {
  return !isSessionExpired(error) && failureCount < 3;
}
